import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  Trophy, Calendar, Clock, Users, ArrowLeft, ChevronRight,
  Play, Timer, Award, Code, CheckCircle, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContestsPage = () => {
  const [contests, setContests] = useState({ upcoming: [], ongoing: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      const [upcoming, ongoing, past] = await Promise.all([
        axios.get(`${API}/coding/contests?status=upcoming`),
        axios.get(`${API}/coding/contests?status=ongoing`),
        axios.get(`${API}/coding/contests?status=past`)
      ]);
      setContests({
        upcoming: upcoming.data.contests || [],
        ongoing: ongoing.data.contests || [],
        past: past.data.contests || []
      });
    } catch (err) {
      console.error('Failed to fetch contests:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const getTimeUntil = (dateStr) => {
    const now = new Date();
    const target = new Date(dateStr);
    const diff = target - now;
    
    if (diff < 0) return 'Started';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate - startDate;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes} minutes`;
  };

  const ContestCard = ({ contest, status }) => (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all group">
      <div className={`h-2 ${
        status === 'ongoing' ? 'bg-emerald-500' :
        status === 'upcoming' ? 'bg-indigo-500' :
        'bg-slate-300'
      }`}></div>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl ${
            status === 'ongoing' ? 'bg-emerald-100' :
            status === 'upcoming' ? 'bg-indigo-100' :
            'bg-slate-100'
          }`}>
            <Trophy className={
              status === 'ongoing' ? 'text-emerald-600' :
              status === 'upcoming' ? 'text-indigo-600' :
              'text-slate-500'
            } size={24} />
          </div>
          
          <div className="flex items-center gap-2">
            {contest.is_rated && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                Rated
              </span>
            )}
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              status === 'ongoing' ? 'bg-emerald-100 text-emerald-700' :
              status === 'upcoming' ? 'bg-indigo-100 text-indigo-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              {status === 'ongoing' ? 'Live Now' : status === 'upcoming' ? 'Upcoming' : 'Ended'}
            </span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
          {contest.title}
        </h3>
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
          {contest.description || 'Join this exciting coding contest and compete with developers!'}
        </p>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm">
            <Calendar className="text-slate-400" size={16} />
            <span className="text-slate-600">{formatDate(contest.start_time)}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Clock className="text-slate-400" size={16} />
            <span className="text-slate-600">
              {formatTime(contest.start_time)} - {formatTime(contest.end_time)}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Timer className="text-slate-400" size={16} />
            <span className="text-slate-600">
              Duration: {getDuration(contest.start_time, contest.end_time)}
            </span>
          </div>
        </div>

        {status === 'upcoming' && (
          <div className="bg-indigo-50 rounded-xl p-4 mb-4">
            <div className="text-xs text-indigo-600 mb-1">Starts in</div>
            <div className="text-2xl font-bold text-indigo-700">
              {getTimeUntil(contest.start_time)}
            </div>
          </div>
        )}

        <Link
          to={`/coding/contest/${contest.id}`}
          className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-colors ${
            status === 'ongoing' 
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
              : status === 'upcoming'
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
              : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
          }`}
          data-testid={`contest-${contest.id}-btn`}
        >
          {status === 'ongoing' ? (
            <>
              <Play size={18} />
              Enter Contest
            </>
          ) : status === 'upcoming' ? (
            <>
              <Calendar size={18} />
              View Details
            </>
          ) : (
            <>
              <Award size={18} />
              View Results
            </>
          )}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Link to="/coding" className="text-slate-400 hover:text-slate-600 transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Coding Contests</h1>
              <p className="text-slate-600 mt-1">Compete with developers from around the world</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
            {[
              { id: 'ongoing', label: 'Live Now', count: contests.ongoing.length },
              { id: 'upcoming', label: 'Upcoming', count: contests.upcoming.length },
              { id: 'past', label: 'Past', count: contests.past.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
                <div className="h-12 w-12 bg-slate-200 rounded-xl mb-4"></div>
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-6"></div>
                <div className="h-12 bg-slate-200 rounded-xl"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Live Contests Banner */}
            {activeTab === 'ongoing' && contests.ongoing.length > 0 && (
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 mb-8 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  <span className="font-semibold">Live Contests Available</span>
                </div>
                <p className="text-emerald-100">
                  {contests.ongoing.length} contest{contests.ongoing.length > 1 ? 's are' : ' is'} currently running. Jump in now!
                </p>
              </div>
            )}

            {/* Contest Grid */}
            {contests[activeTab].length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {contests[activeTab].map((contest) => (
                  <ContestCard key={contest.id} contest={contest} status={activeTab} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                {activeTab === 'ongoing' ? (
                  <>
                    <AlertCircle className="mx-auto text-slate-300 mb-4" size={48} />
                    <h3 className="font-semibold text-slate-700 mb-2">No Live Contests</h3>
                    <p className="text-slate-500 mb-4">Check back soon or view upcoming contests</p>
                    <button
                      onClick={() => setActiveTab('upcoming')}
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      View Upcoming Contests
                    </button>
                  </>
                ) : activeTab === 'upcoming' ? (
                  <>
                    <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
                    <h3 className="font-semibold text-slate-700 mb-2">No Upcoming Contests</h3>
                    <p className="text-slate-500">New contests will be announced soon. Stay tuned!</p>
                  </>
                ) : (
                  <>
                    <Trophy className="mx-auto text-slate-300 mb-4" size={48} />
                    <h3 className="font-semibold text-slate-700 mb-2">No Past Contests</h3>
                    <p className="text-slate-500">Contest history will appear here</p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Contest Detail Page Component
export const ContestDetailPage = () => {
  const { contestId } = useParams();
  const { user } = useAuth();
  const [contest, setContest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('problems');

  useEffect(() => {
    fetchContest();
  }, [contestId]);

  const fetchContest = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const [contestRes, lbRes] = await Promise.all([
        axios.get(`${API}/coding/contests/${contestId}`, { headers }),
        axios.get(`${API}/coding/contests/${contestId}/leaderboard`)
      ]);
      setContest(contestRes.data);
      setLeaderboard(lbRes.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch contest:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyStyle = (diff) => {
    switch (diff) {
      case 'easy': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'hard': return 'text-rose-600 bg-rose-50 border-rose-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto text-slate-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-slate-700">Contest Not Found</h2>
          <Link to="/coding/contests" className="text-indigo-600 hover:text-indigo-700 mt-2 inline-block">
            Back to Contests
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/coding/contests" className="text-slate-400 hover:text-slate-600">
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              {contest.is_rated && (
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                  Rated
                </span>
              )}
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                contest.status === 'ongoing' ? 'bg-emerald-100 text-emerald-700' :
                contest.status === 'upcoming' ? 'bg-indigo-100 text-indigo-700' :
                'bg-slate-100 text-slate-600'
              }`}>
                {contest.status === 'ongoing' ? 'Live Now' : 
                 contest.status === 'upcoming' ? 'Upcoming' : 'Ended'}
              </span>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{contest.title}</h1>
          <p className="text-slate-600">{contest.description}</p>

          <div className="flex gap-6 mt-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {new Date(contest.start_time).toLocaleDateString()}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} />
              {new Date(contest.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
              {new Date(contest.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="flex items-center gap-2">
              <Code size={16} />
              {contest.problem_details?.length || 0} Problems
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('problems')}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === 'problems' 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Problems
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === 'leaderboard' 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Leaderboard
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'problems' ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {contest.status === 'upcoming' ? (
              <div className="p-12 text-center">
                <Clock className="mx-auto text-slate-300 mb-4" size={48} />
                <h3 className="font-semibold text-slate-700 mb-2">Contest Hasn't Started Yet</h3>
                <p className="text-slate-500">Problems will be revealed when the contest begins</p>
              </div>
            ) : contest.problem_details?.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {contest.problem_details.map((problem, idx) => (
                  <Link
                    key={problem.id}
                    to={`/coding/problem/${problem.id}?contest=${contestId}`}
                    className="flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-semibold">
                        {String.fromCharCode(65 + idx)}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{problem.title}</div>
                        <div className="flex gap-2 mt-1">
                          {problem.tags?.slice(0, 2).map((tag, i) => (
                            <span key={i} className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getDifficultyStyle(problem.difficulty)}`}>
                        {problem.difficulty?.toUpperCase()}
                      </span>
                      <ChevronRight className="text-slate-400" size={20} />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Code className="mx-auto text-slate-300 mb-4" size={48} />
                <h3 className="font-semibold text-slate-700">No Problems Available</h3>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-sm font-medium text-slate-500">
              <div className="col-span-1 text-center">Rank</div>
              <div className="col-span-5">Participant</div>
              <div className="col-span-3 text-center">Problems Solved</div>
              <div className="col-span-3 text-center">Points</div>
            </div>
            
            {leaderboard.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {leaderboard.map((entry, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50">
                    <div className="col-span-1 text-center font-semibold text-slate-600">
                      {entry.rank}
                    </div>
                    <div className="col-span-5 font-medium text-slate-900">
                      {entry.user_id}
                    </div>
                    <div className="col-span-3 text-center text-slate-600">
                      {entry.problems_solved}
                    </div>
                    <div className="col-span-3 text-center font-bold text-indigo-600">
                      {entry.total_points}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Trophy className="mx-auto text-slate-300 mb-4" size={48} />
                <h3 className="font-semibold text-slate-700">No Submissions Yet</h3>
                <p className="text-slate-500">Be the first to solve a problem!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContestsPage;
